import { isEqual } from 'lodash-es'
import { getColorByString } from '@/utils/color'
import { formatMoney } from '@/utils/currency'
import {
  getCountryService,
  getMonitoringService,
  getPermissionsService,
  getServerSubscriptionService,
  getUserService,
  getUserSettingsService,
} from '@/core/container/ioc'
import { UserRole, type UserDTO } from '@/services/domain/user/types'
import type { TServerEvent } from '@/services/transport/serverEvents'
import { computed, watch } from '@/composition/vue/compositionApi'
import User from '@/data/models/domain/User'
import type { TmIconType } from '@/components/shared/types'
import { APP_DEFAULT_LOCALE } from '@/services/translateService.types'
import type UserSettings from '@/data/models/domain/UserSettings'

const systemAccountStateChangedEvent = 'systemAccountStateChanged'
const userBalanceChangedEvent = 'userBalanceChanged'

const currentUser = computed(() => {
  const user = getUserService().getCurrentUserOrNull(['timezone'])
  if (user) {
    return user
  }
  // here we don't have a current user yet
  return new User({
    subaccountType: UserRole.USER,
  })
})

// Settings can arrive after the User, so we have to recalculate the spread
const currentUserSettingsOrNull = computed(() => getUserSettingsService().currentUserSettingsOrNull())
watch([currentUser, currentUserSettingsOrNull], ([user, userSettings]) => {
  getMonitoringService().setUser({ ...user, ...userSettings } as User & UserSettings)
})

const userFullName = computed(() => currentUser.value.fullName)
const currency = computed(() => currentUser.value.currency)
const avatarColor = computed(() => getColorByString(currentUser.value.id))
const balanceWithCurrency = computed(() => formatMoneyForCurrentUser(currentUser.value.balance))
const pendingAmount = computed(() => currentUser.value.pendingAmount)
const pendingAmountWithCurrency = computed(() => formatMoneyForCurrentUser(currentUser.value.pendingAmount))
const isLowBalance = computed(() => currentUser.value.isCriticallyLowBalance)
const email = computed(() => currentUser.value.email)

let prevPayload: TServerEvent<typeof systemAccountStateChangedEvent>['payload']

const handleAccountStateChanged = (event: TServerEvent<typeof systemAccountStateChangedEvent>) => {
  const userService = getUserService()
  const data = event.payload
  if (data.subaccountType !== currentUser.value.subaccountType) {
    getPermissionsService().getPermissions()
  }
  if (!isEqual(data, prevPayload)) {
    // to debouncing (the event comes often with the same payload)
    const user = { ...currentUser.value, ...data }
    userService.updateCurrentUserInStore(user as UserDTO)
  }
  prevPayload = data
}

const handleSystemAccountAdditionalFields = (event: TServerEvent<'systemAccountAdditionalFields'>) => {
  getUserSettingsService().updateCurrentUserSettingsInStore(event.payload)
}

const handleUserBalanceChanged = (event: TServerEvent<typeof userBalanceChangedEvent>) => {
  getUserService().updateCurrentUserInStore({
    balance: event.payload.balance,
    pendingAmount: event.payload.pendingAmount,
  })
}

let isSubscribed = false

export const useCurrentUser = () => {
  if (!isSubscribed) {
    // here we already have a websocket connection to subscribe to this event once
    const serverSubscriptionService = getServerSubscriptionService()
    serverSubscriptionService.subscribe(systemAccountStateChangedEvent, handleAccountStateChanged)
    serverSubscriptionService.subscribe(userBalanceChangedEvent, handleUserBalanceChanged)
    serverSubscriptionService.subscribe('systemAccountAdditionalFields', handleSystemAccountAdditionalFields)
    isSubscribed = true
  }
  return {
    currentUser,
    userFullName,
    avatarColor,
    balanceWithCurrency,
    pendingAmount,
    pendingAmountWithCurrency,
    currency,
    isLowBalance,
    email,
  }
}

export const useCurrentUserCountryFormatted = () => {
  const countryService = getCountryService()

  const currentUserCountryFormatted = computed(() => {
    const countryId = currentUser.value.countryId
    return countryService.getFormattedCountryName(countryId)
  })

  return {
    currentUserCountryFormatted,
  }
}

export const useCurrentUserPageVisibilitySettings = () => {
  const { currentUserSettings } = useCurrentUserSettings()
  return {
    showSenderIds: computed(() => currentUserSettings.value.showSenderIds),
    showTenDlcTab: computed(() => currentUserSettings.value.showTenDlcTab),
    showTollFreeTab: computed(() => currentUserSettings.value.showTollFreeTab),
  }
}

export const useCurrentUserAlerts = () => {
  const { currentUserSettings } = useCurrentUserSettings()

  const warningIcon: TmIconType = {
    name: 'warning',
    size: 'xLarge',
    class: 'orange--text',
  }
  const doneIcon: TmIconType = {
    name: 'check_circle',
    size: 'xLarge',
    class: 'success--text',
  }

  return {
    warningIcon,
    doneIcon,
    showTenDlcAlert: computed(() => currentUserSettings.value.showTenDlcAlert),
    showTollFreeAlert: computed(() => currentUserSettings.value.showTollFreeAlert),
  }
}

/**
 * convert value of amount to string in current user's currency
 * only for use at the UI level (for other levels, use NumericService.formatMoneyForCurrentUser)
 * @param amount
 * @param dp
 * @returns $1.23 or €0.234
 */
export function formatMoneyForCurrentUser(amount: number, dp?: number): string {
  return formatMoney(amount, currentUser.value.currency?.id ?? 'USD', APP_DEFAULT_LOCALE, dp)
}

export const useCurrentUserSettings = () => {
  const currentUserSettings = computed(() => getUserSettingsService().currentUserSettingsOrFail())

  return {
    currentUserSettings,
  }
}

export const useCurrentUserParentId = () => {
  const { currentUser: curUser } = useCurrentUser()
  const { currentUserSettings } = useCurrentUserSettings()

  const isParentUser = computed(() => curUser.value.subaccountType === UserRole.SUPER_ADMIN)
  const parentId = computed(() => currentUserSettings.value.parentId)
  const parentIdNotSelf = computed(() => (isParentUser.value ? undefined : parentId.value))

  return {
    isParentUser,
    parentId,
    parentIdNotSelf,
  }
}
